import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProfileService, ScdService, InformationService } from '@app-root-services';
import { map, switchMap, catchError, withLatestFrom, concatMap, switchMapTo } from 'rxjs/operators';
import * as profileSelectors from '../selectors/profile.selector';
import * as userSelectors from '../selectors/user.selector';
import { documentActions, errorActions, profileActions } from '../actions';
import { IError } from '@app-root-interfaces';
import { of, iif } from 'rxjs';
import { addDays, isAfter } from 'date-fns';
import { Store, select } from '@ngrx/store';
import { appReducer } from '@app-root-store';
import { NoProfilesError } from '../../exceptions';
import { ThemeService } from '../../root-services/theme.service';
import { ProfileHelper } from '../../../shared/helper/profile.helper';
import { IProfile, ProfileType } from '@scf-monorepo/interfaces';

@Injectable()
export class ProfileEffects {
    private actions$ = inject(Actions);
    private profileService = inject(ProfileService);
    private informationService = inject(InformationService);
    private scdService = inject(ScdService);
    private store = inject<Store<appReducer.AppState>>(Store);
    private theme = inject(ThemeService);

    getScdUserForInitialProfileCreate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.getScdUserForInitialProfileCreate),
            switchMap(({ gid }) =>
                this.scdService.getScdUser(gid).pipe(
                    map((scdUser) => profileActions.createInitialProfile({ scdUser })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: true,
                            text: 'user-error-get-scd'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    createInitialProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.createInitialProfile),
            switchMap(({ scdUser }) =>
                this.profileService.createInitialProfile(scdUser).pipe(
                    switchMap((profile) =>
                        of(
                            profileActions.createInitialProfileSuccess({ profile }),
                            profileActions.setThemeByUserProfile({ profile })
                        )
                    ),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: true,
                            text: 'user-error-save'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    getUserProfiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.getUserProfiles),
            switchMap(({ user }) =>
                this.profileService.getUserProfiles(user).pipe(
                    map((profiles) => {
                        if (!profiles.length) {
                            throw new NoProfilesError();
                        }
                        return profiles;
                    }),
                    switchMap((profiles) =>
                        of(
                            profileActions.checkForProfileUpdate({ user, profiles }),
                            profileActions.setThemeByUserProfile({
                                profile: ProfileHelper.getUserProfile(profiles, user.gid)
                            })
                        )
                    ),
                    catchError((err) => {
                        if (err instanceof NoProfilesError || err.status === 404) {
                            return of(
                                profileActions.getScdUserForInitialProfileCreate({
                                    gid: user.gid
                                })
                            );
                        } else {
                            const error: IError = {
                                obj: err,
                                isCritical: true,
                                text: 'profile-error-get'
                            };
                            return of(errorActions.addError({ error }));
                        }
                    })
                )
            )
        )
    );

    checkActiveProfileForUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.checkForProfileUpdate),
            switchMap(({ user, profiles }) => {
                const activeProfile = ProfileHelper.getActiveProfile(profiles, user.gid);

                if (!this.shouldUpdateProfile(activeProfile)) {
                    return of(profileActions.getUserProfilesSuccess({ profiles }));
                }

                return this.scdService.getScdUser(activeProfile.gid).pipe(
                    map((scdUser) =>
                        profileActions.updateProfileFromScd({
                            scdUser,
                            profiles,
                            profileId: activeProfile.id,
                            user
                        })
                    ),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-update-selected'
                        };
                        return of(
                            profileActions.getUserProfilesSuccess({ profiles }),
                            errorActions.addError({ error })
                        );
                    })
                );
            })
        )
    );

    private shouldUpdateProfile(profile: IProfile): boolean {
        const deadline = addDays(new Date(profile.updatedAt), 1);
        return profile.type === ProfileType.ORIGINAL && isAfter(new Date(), deadline);
    }

    loadDriveForUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                profileActions.selectProfile,
                profileActions.getUserProfilesSuccess,
                profileActions.createInitialProfileSuccess
            ),
            map(() => documentActions.getDrive())
        );
    });

    checkSelectedProfilForScdUpdate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(profileActions.selectProfile),
            withLatestFrom(
                this.store.pipe(select(profileSelectors.selectProfileSelectedProfile)),
                this.store.pipe(select(profileSelectors.selectProfileProfiles)),
                this.store.pipe(select(userSelectors.selectUserCurrentUser))
            ),
            concatMap(([{ id }, profile, profiles, user]) => {
                if (!this.shouldUpdateProfile(profile)) {
                    return of(profileActions.doNothing());
                }

                return this.scdService.getScdUser(profile.gid).pipe(
                    map((scdUser) =>
                        profileActions.updateProfileFromScd({
                            scdUser,
                            profiles,
                            profileId: id,
                            user,
                            isActive: profile.isActive
                        })
                    ),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-update-selected'
                        };
                        return of(errorActions.addError({ error }));
                    })
                );
            })
        );
    });

    updateProfileFromScd$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.updateProfileFromScd),
            switchMap(({ scdUser, profiles, profileId, user, isActive }) =>
                this.profileService.updateProfileFromScd(scdUser, profileId, user.gid, isActive).pipe(
                    map((profile) =>
                        profiles.reduce((agg, item) => {
                            if (item.id === profile.id) {
                                return [...agg, profile];
                            } else {
                                return [...agg, item];
                            }
                        }, [])
                    ),
                    map((updatedProfiles) =>
                        profileActions.getUserProfilesSuccess({
                            profiles: updatedProfiles
                        })
                    ),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-update-selected'
                        };
                        return of(
                            profileActions.getUserProfilesSuccess({
                                profiles
                            }),
                            errorActions.addError({ error })
                        );
                    })
                )
            )
        )
    );

    activateProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.activateProfile),
            withLatestFrom(this.store.pipe(select(profileSelectors.selectProfileActiveProfile))),
            switchMap(([{ profile }, activeProfile]) =>
                this.profileService.activateProfile(profile, activeProfile).pipe(
                    map((profiles) => profileActions.activateProfileSuccess({ profiles })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-set-standard'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    copyProfileAndSetActive$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.copyProfileAndSetActive),
            withLatestFrom(this.store.pipe(select(profileSelectors.selectProfileSelectedProfile))),
            switchMap(([{ profile }, selectedProfile]) =>
                this.profileService.copyProfileAndSetActive(profile, selectedProfile).pipe(
                    map((profiles: IProfile[]) => profileActions.copyProfileAndSetActiveSuccess({ profiles })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-copy'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    updateProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.updateProfile),
            withLatestFrom(this.store.pipe(select(profileSelectors.selectProfileSelectedProfile))),
            concatMap(([{ profile }, selectedProfile]) =>
                this.profileService.updateProfile({ ...selectedProfile, ...profile }).pipe(
                    map((data) => profileActions.updateProfileSuccess({ profile: data })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-update'
                        };
                        return of(errorActions.addError({ error }), profileActions.updateProfileFailure());
                    })
                )
            )
        )
    );

    updateAdminProfile$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(profileActions.updateAdminProfile),
            withLatestFrom(this.store.pipe(select(profileSelectors.selectProfileProfiles))),
            concatMap(([{ profile }, profiles]) => {
                const originalProfile = profiles.find((pro) => pro.id === profile.id);
                if (!originalProfile) {
                    const error: IError = {
                        obj: null,
                        isCritical: false,
                        text: 'profile-error-update'
                    };
                    return of(errorActions.addError({ error }));
                }
                return this.profileService.updateProfile({ ...originalProfile, ...profile }).pipe(
                    map((updatedProfile) =>
                        profileActions.updateAdminProfileSuccess({
                            profile: updatedProfile
                        })
                    ),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-update'
                        };
                        return of(errorActions.addError({ error }));
                    })
                );
            })
        );
    });

    // Löscht ein Profil, wenn das Profil bisher aktiv war muss erst ein anderes Profil aktiv geschalten werden.
    deleteProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.deleteProfile),
            concatMap(({ profile }) =>
                iif(
                    () => profile.isActive,
                    of(profileActions.changeSelectionAndDelete({ profile })),
                    this.profileService.deleteProfile(profile).pipe(
                        map(() => profileActions.deleteProfileSuccess({ profile })),
                        catchError((err) => {
                            const error: IError = {
                                obj: err,
                                isCritical: false,
                                text: 'profile-error-delete'
                            };
                            return of(errorActions.addError({ error }));
                        })
                    )
                )
            )
        )
    );

    changeSelectionAndDelete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.changeSelectionAndDelete),
            withLatestFrom(this.store.pipe(select(profileSelectors.selectProfileUserProfile))),
            concatMap(([{ profile }, userProfile]) =>
                this.profileService.changeSelectionAndDelete(profile, userProfile).pipe(
                    map((newUserProfile: IProfile) =>
                        profileActions.changeSelectionAndDeleteSuccess({
                            deletedProfile: profile,
                            userProfile: newUserProfile
                        })
                    ),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-delete'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    createSCDProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(profileActions.createSCDProfile),
            withLatestFrom(this.store.pipe(select(userSelectors.selectUserCurrentUser))),
            concatMap(([{ scdUser }, user]) =>
                this.profileService.createSCDProfile(scdUser, user.gid).pipe(
                    map((profile) => profileActions.createSCDProfileSuccess({ profile })),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: 'profile-error-create'
                        };
                        return of(errorActions.addError({ error }));
                    })
                )
            )
        )
    );

    checkProfileInformations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(profileActions.checkProfileInformations),
            withLatestFrom(this.store.pipe(select(profileSelectors.selectProfileSelectedProfile))),
            switchMap(([, profile]) =>
                this.informationService.checkProfileInformation(profile).pipe(
                    switchMapTo(
                        of(
                            profileActions.checkProfileInformationsSuccess(),
                            profileActions.setProfileValidity({ profile, valid: true })
                        )
                    ),
                    catchError((err) => {
                        const error: IError = {
                            obj: err,
                            isCritical: false,
                            text: err,
                            timer: 0
                        };
                        return of(
                            profileActions.checkProfileInformationsFailure(),
                            errorActions.addError({ error }),
                            profileActions.setProfileValidity({ profile, valid: false })
                        );
                    })
                )
            )
        );
    });

    setUserTheme$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(profileActions.setThemeByUserProfile),
            switchMap(({ profile }) =>
                this.informationService.getLegals(profile).pipe(
                    map((legals) => {
                        const clientId = legals?.clientId ? (legals.clientId as string) : undefined;
                        this.theme.setThemeByClientId(clientId, profile.mail);
                    }),
                    map(() => profileActions.setThemeSuccess())
                )
            )
        );
    });
}
